import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Input,
  Tabs,
  Modal,
  Radio,
  message,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "../../libs/api";

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_TABS = [
  { key: "PENDING_REQUEST", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

const STATE_TAG = {
  PENDING_REQUEST: { color: "gold", text: "Pending" },
  APPROVED: { color: "green", text: "Approved" },
  REJECTED: { color: "red", text: "Rejected" },
};

// Platform users (they see multiple partners) — UX hint only; the backend is
// the authority on scope. roleType is not security here, just column display.
function isPlatformRole() {
  const rt = (localStorage.getItem("roleType") || "").toUpperCase();
  return rt === "SUPERADMIN" || rt === "INTERNAL";
}

// Read-only roles (e.g. Partner Viewer) hold binding:read but not binding:approve; showing
// them a live Review button would just 403 on click.
function canApprove() {
  if ((localStorage.getItem("roleType") || "").toUpperCase() === "SUPERADMIN") return true;
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]").includes("binding:approve");
  } catch (_) {
    return false;
  }
}

export default function PendingBindings() {
  const [status, setStatus] = useState("PENDING_REQUEST");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const platform = isPlatformRole();
  const mayApprove = canApprove();

  // Review modal state
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null); // the binding row
  const [approve, setApprove] = useState(true);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/partner/bindings", {
        params: { status, search: search || undefined, limit: 500 },
      });
      setRows(res.data?.data || []);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load bindings");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = (row) => {
    setReviewTarget(row);
    setApprove(true);
    setNote("");
    setReviewOpen(true);
  };

  const submitReview = async () => {
    if (!reviewTarget) return;
    setSubmitting(true);
    try {
      const res = await api.post(
        `/api/partner/bindings/${reviewTarget.id}/review`,
        { approve, note: note || undefined }
      );
      if (res.data?.success) {
        message.success(
          `${approve ? "Approved" : "Rejected"} binding for ${
            reviewTarget.name || reviewTarget.email || reviewTarget.id
          }`
        );
        setReviewOpen(false);
        load();
      } else {
        message.error(res.data?.message || "Review failed");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Review failed");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Customer",
      dataIndex: "name",
      key: "name",
      render: (name, r) => (
        <div>
          <div>{name || <Text type="secondary">—</Text>}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>
        </div>
      ),
    },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => v || "—" },
    { title: "NMI", dataIndex: "nmi", key: "nmi", render: (v) => v || "—" },
    ...(platform
      ? [{
          title: "Partner",
          key: "partner",
          render: (_, r) =>
            r.pending_partner_name || r.bound_partner_name ||
            <Text type="secondary">{r.pending_partner_id || r.company_id || "—"}</Text>,
        }]
      : []),
    {
      title: "Status",
      dataIndex: "binding_state",
      key: "binding_state",
      render: (s) => {
        const t = STATE_TAG[s] || { color: "default", text: s || "—" };
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    {
      title: "Requested / Updated",
      dataIndex: "update_time",
      key: "update_time",
      render: (v, r) => {
        const ts = v || r.create_time;
        return ts ? new Date(ts).toLocaleString() : "—";
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) =>
        r.binding_state === "PENDING_REQUEST" && mayApprove ? (
          <Button type="primary" size="small" onClick={() => openReview(r)}>
            Review
          </Button>
        ) : (
          <Tooltip
            title={
              r.binding_state !== "PENDING_REQUEST"
                ? "Only pending requests can be actioned"
                : "You have read-only access to binding approvals"
            }
          >
            <span>
              <Button size="small" disabled>Review</Button>
            </span>
          </Tooltip>
        ),
    },
  ];

  return (
    <PageLayout active="customerApprovals">
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Customer Binding Approvals</Title>
          <Text type="secondary">
            {platform
              ? "All partners' customer binding requests (you may approve on a partner's behalf)."
              : "Customers who chose your company in the app and are awaiting your approval."}
          </Text>
        </div>
        <Space>
          <Input
            allowClear
            placeholder="Search name / email / NMI"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={load}
            style={{ width: 260 }}
          />
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Space>
      </div>

      <Tabs
        activeKey={status}
        onChange={setStatus}
        items={STATUS_TABS.map((t) => ({ key: t.key, label: t.label }))}
      />

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} customer(s)` }}
        size="middle"
      />

      <Modal
        title="Review Binding Request"
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        onOk={submitReview}
        confirmLoading={submitting}
        okText={approve ? "Approve" : "Reject"}
        okButtonProps={{ danger: !approve }}
      >
        {reviewTarget && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Text strong>{reviewTarget.name || reviewTarget.email || reviewTarget.id}</Text>
              <br />
              <Text type="secondary">{reviewTarget.email}{reviewTarget.nmi ? ` · NMI ${reviewTarget.nmi}` : ""}</Text>
            </div>
            <Radio.Group value={approve} onChange={(e) => setApprove(e.target.value)}>
              <Radio.Button value={true}><CheckCircleOutlined /> Approve</Radio.Button>
              <Radio.Button value={false}><CloseCircleOutlined /> Reject</Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {approve
                ? "Approving binds this customer to your company and notifies the app."
                : "Rejecting leaves the customer unbound; they can request again from the app."}
            </Text>
            <TextArea
              rows={3}
              placeholder="Note (optional — recorded in the audit log)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
          </div>
        )}
      </Modal>
    </Card>
    </PageLayout>
  );
}
